import { useEffect, useState } from 'react';
import { Button } from './button';
import { Checkbox, CheckboxField } from './checkbox';
import { Label } from './fieldset';
import { Input } from './input';
import { Spinner } from './spinner';
import { Text } from './text';

/**
 * Shared signing UI: an inline PDF preview, an electronic-records consent
 * checkbox, and a typed-name field with a live preview in a signature font.
 *
 * Used by both the in-app {@link DocumentSignDialog} (session auth) and the
 * public magic-link signing page (token auth) so the signing experience — and
 * the exact content the signer attests to — is identical in both entry points.
 */

// Matches the PDF's rendered signature block, which uses Dancing Script
// (see backend/templates/documents/_signature_block.html).
const SIGNATURE_FONT_FAMILY = "'Dancing Script', 'Segoe Script', cursive";
const SIGNATURE_FONT_HREF =
  'https://fonts.googleapis.com/css2?family=Dancing+Script:wght@600&display=swap';
const SIGNATURE_FONT_LINK_ID = 'signature-font-dancing-script';

const CONSENT_LABEL =
  'I agree to use electronic records and signatures, and that typing my name below constitutes my legal signature on this document.';

/** Load the signature font once, lazily, so the rest of the app never pays for it. */
function useSignatureFont() {
  useEffect(() => {
    if (document.getElementById(SIGNATURE_FONT_LINK_ID)) return;
    const link = document.createElement('link');
    link.id = SIGNATURE_FONT_LINK_ID;
    link.rel = 'stylesheet';
    link.href = SIGNATURE_FONT_HREF;
    document.head.appendChild(link);
  }, []);
}

type SignatureSignBodyProps = {
  /** Presigned inline URL for the PDF, or null while loading. */
  documentUrl: string | null;
  documentLabel: string;
  isSubmitting: boolean;
  onSign: (signedName: string) => void;
  onCancel?: () => void;
  /** When the viewer's participant has already signed, show a read-only note. */
  alreadySigned?: boolean;
};

export function SignatureSignBody({
  documentUrl,
  documentLabel,
  isSubmitting,
  onSign,
  onCancel,
  alreadySigned = false,
}: SignatureSignBodyProps) {
  useSignatureFont();
  const [signedName, setSignedName] = useState('');
  const [consent, setConsent] = useState(false);

  const trimmedName = signedName.trim();
  const canSign = !alreadySigned && consent && trimmedName.length > 0 && !isSubmitting;

  const handleSign = () => {
    if (canSign) onSign(trimmedName);
  };

  return (
    <div className="tw:flex tw:flex-col tw:gap-4">
      {/* Inline document preview */}
      <div className="tw:h-[45vh] tw:min-h-64 tw:w-full tw:overflow-hidden tw:rounded-md tw:border tw:border-zinc-300">
        {documentUrl ? (
          <iframe
            src={documentUrl}
            title={`Document preview: ${documentLabel}`}
            className="tw:h-full tw:w-full"
            data-testid="signature-document-preview"
          />
        ) : (
          <div className="tw:flex tw:h-full tw:items-center tw:justify-center">
            <Spinner variant="zinc" />
          </div>
        )}
      </div>

      {alreadySigned ? (
        <div className="tw:rounded-md tw:border tw:border-stoplight-success/40 tw:bg-stoplight-success/10 tw:p-4">
          <Text className="tw:font-medium tw:text-stoplight-success">
            <i className="fas fa-circle-check tw:mr-2" />
            This document has already been signed.
          </Text>
        </div>
      ) : (
        <>
          <CheckboxField>
            <Checkbox
              color="brandgreen1"
              checked={consent}
              onChange={setConsent}
              disabled={isSubmitting}
              data-testid="signature-consent"
            />
            <Label>{CONSENT_LABEL}</Label>
          </CheckboxField>

          <div>
            <label
              htmlFor="signature-name"
              className="tw:mb-1 tw:block tw:text-sm tw:font-medium tw:text-zinc-700"
            >
              Type your full name to sign
            </label>
            <Input
              id="signature-name"
              type="text"
              value={signedName}
              onChange={(e) => setSignedName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSign()}
              placeholder="Full name"
              disabled={isSubmitting}
              autoComplete="off"
              className="tw:w-full"
              data-testid="signature-name-input"
            />
            {/* Live signature preview in the same script the PDF renders. */}
            <div
              className="tw:mt-2 tw:flex tw:min-h-[3rem] tw:items-center tw:rounded-md tw:border tw:border-dashed tw:border-zinc-300 tw:px-4 tw:py-2"
              aria-hidden={!trimmedName}
            >
              {trimmedName ? (
                <span
                  className="tw:text-3xl tw:leading-none tw:text-zinc-900"
                  style={{ fontFamily: SIGNATURE_FONT_FAMILY }}
                  data-testid="signature-preview"
                >
                  {trimmedName}
                </span>
              ) : (
                <span className="tw:text-sm tw:text-zinc-400">Your signature will appear here</span>
              )}
            </div>
          </div>

          <div className="tw:flex tw:items-center tw:justify-end tw:gap-3 tw:pt-1">
            {onCancel && (
              <Button outline type="button" onClick={onCancel} disabled={isSubmitting}>
                Cancel
              </Button>
            )}
            <Button
              type="button"
              color="brandgreen0"
              onClick={handleSign}
              disabled={!canSign}
              data-testid="signature-sign-button"
            >
              {isSubmitting ? (
                <div className="tw:flex tw:items-center tw:gap-2">
                  <Spinner size="sm" variant="white" />
                  <span>Signing…</span>
                </div>
              ) : (
                <>
                  <i className="fas fa-signature" />
                  Click to Sign
                </>
              )}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
